import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { DeleteBannerMethod } from "../../redux/slices/website";
import BannerForm from "./bannerForm";
import SectionHeader from "./sectionheader";
import CardListing from "../cardlisting";

export default function Banner() {
  const [selected, setSelected] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [fullPreview, setFullPreview] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const dispatch = useDispatch();
  const banners = useSelector((state) => state.website.banners);

  const sortedBanners = useMemo(() => {
    return [...banners].sort((a, b) => b.order - a.order);
  }, [banners]);

  const handleDeleteClick = (banner) => {
    setSelected(banner);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selected) return;

    try {
      setDeleting(true);
      await dispatch(DeleteBannerMethod(selected.id));

      setDeleteConfirmOpen(false);
      setSelected(null);
      setDeleting(false);
    } catch (err) {
      console.error("Delete error:", err);
      //   setError("Failed to delete banner. Please try again.");
      setDeleteConfirmOpen(false);
      setSelected(null);
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setSelected(null);
  };

  return (
    <>
      <SectionHeader
        title="banner"
        heading="Website Banners"
        subHeading="Manage banner images displayed on your website"
        onAdd={() => {
          setFormOpen(true);
        }}
      />

      {/* BANNER GRID */}
      <CardListing
        title="banner"
        data={sortedBanners}
        size={{
          height: 200,
          width: {
            xs: 12,
            sm: 6,
            md: 5,
            lg: 4,
          },
        }}
        onEdit={(item) => {
          setSelected(item);
          setFormOpen(true);
        }}
        onDelete={handleDeleteClick}
        onAdd={() => setFormOpen(true)}
        onPreview={setFullPreview}
      />

      {/* ADD / EDIT MODAL */}
      <BannerForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setSelected(null);
        }}
        selected={selected}
      />

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !deleting && handleDeleteCancel()}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selected?.url && (
            <Box mb={2}>
              <img
                src={selected.url}
                alt="Banner to delete"
                style={{
                  width: "100%",
                  height: 150,
                  objectFit: "cover",
                  borderRadius: 8,
                }}
              />
            </Box>
          )}
          <Typography>
            Are you sure you want to delete this banner? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleting}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            sx={{ borderRadius: 2 }}
            disabled={deleting}
            loading={deleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* FULLSCREEN PREVIEW */}
      <Dialog
        open={Boolean(fullPreview)}
        onClose={() => setFullPreview(null)}
        maxWidth={true}
        PaperProps={{
          sx: { borderRadius: 3, bgcolor: "transparent", boxShadow: "none" },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <IconButton
            onClick={() => setFullPreview(null)}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              bgcolor: "rgba(255,255,255,0.9)",
              color: "text.primary",
              "&:hover": {
                bgcolor: "white",
              },
              boxShadow: 3,
            }}
          >
            <Close />
          </IconButton>

          <Box
            component="img"
            src={fullPreview}
            alt="Full Preview"
            sx={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              width: "auto",
              height: "auto",
              borderRadius: 2,
              boxShadow: 6,
            }}
          />
        </Box>
      </Dialog>
    </>
  );
}
